import { useEffect, useState, useRef } from 'react';

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;
  opacity: number;
  color: string;
}

interface CodeFragment {
  id: number;
  x: number;
  y: number;
  text: string;
  opacity: number;
  speed: number;
}

const codeFragments = [
  'async', 'await', 'function', 'const', 'return',
  '=>', '{ }', '( )', 'import', 'export',
  'AI', 'GPT', 'Claude', 'Code', 'Build',
  '<>', '</>', '...', '===', '&&',
  'React', 'Next', 'Node', 'API', 'DB',
];

const colors = ['#6366f1', '#8b5cf6', '#a855f7', '#22c55e', '#3b82f6', '#f59e0b'];

export function HeroBackground() {
  const [particles, setParticles] = useState<Particle[]>([]);
  const [codeItems, setCodeItems] = useState<CodeFragment[]>([]);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const containerRef = useRef<HTMLDivElement>(null);

  // Initialize particles and code fragments
  useEffect(() => {
    const initialParticles: Particle[] = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 2 + Math.random() * 3,
      speedX: (Math.random() - 0.5) * 0.15,
      speedY: (Math.random() - 0.5) * 0.15,
      opacity: 0.1 + Math.random() * 0.3,
      color: colors[Math.floor(Math.random() * colors.length)],
    }));
    setParticles(initialParticles);

    const initialCode: CodeFragment[] = Array.from({ length: 12 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      text: codeFragments[Math.floor(Math.random() * codeFragments.length)],
      opacity: 0.03 + Math.random() * 0.06,
      speed: 0.1 + Math.random() * 0.2,
    }));
    setCodeItems(initialCode);
  }, []);

  // Animate particles
  useEffect(() => {
    const interval = setInterval(() => {
      setParticles(prev =>
        prev.map(p => {
          let newX = p.x + p.speedX;
          let newY = p.y + p.speedY;

          // Bounce off edges
          if (newX <= 0 || newX >= 100) p.speedX *= -1;
          if (newY <= 0 || newY >= 100) p.speedY *= -1;

          newX = Math.max(0, Math.min(100, newX));
          newY = Math.max(0, Math.min(100, newY));

          return { ...p, x: newX, y: newY };
        })
      );

      setCodeItems(prev =>
        prev.map(c => ({
          ...c,
          y: c.y <= -5 ? 105 : c.y - c.speed,
          x: c.x + Math.sin(Date.now() / 2000 + c.id) * 0.05,
        }))
      );
    }, 50);

    return () => clearInterval(interval);
  }, []);

  // Track mouse for interactive effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setMousePos({
          x: ((e.clientX - rect.left) / rect.width) * 100,
          y: ((e.clientY - rect.top) / rect.height) * 100,
        });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Calculate connections between nearby particles
  const connections: { x1: number; y1: number; x2: number; y2: number; opacity: number }[] = [];
  particles.forEach((p1, i) => {
    particles.slice(i + 1).forEach(p2 => {
      const dist = Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2));
      if (dist < 15) {
        connections.push({
          x1: p1.x,
          y1: p1.y,
          x2: p2.x,
          y2: p2.y,
          opacity: (1 - dist / 15) * 0.15,
        });
      }
    });
  });

  return (
    <div ref={containerRef} className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Gradient base */}
      <div className="absolute inset-0 bg-gradient-to-b from-accent/5 via-transparent to-transparent" />

      {/* Radial glow following mouse */}
      <div
        className="absolute w-[600px] h-[600px] rounded-full transition-all duration-1000 ease-out"
        style={{
          left: `${mousePos.x}%`,
          top: `${mousePos.y}%`,
          transform: 'translate(-50%, -50%)',
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%)',
        }}
      />

      {/* Secondary glow */}
      <div
        className="absolute w-[400px] h-[400px] rounded-full opacity-30"
        style={{
          left: '70%',
          top: '30%',
          background: 'radial-gradient(circle, rgba(168, 85, 247, 0.2) 0%, transparent 70%)',
        }}
      />

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `
            linear-gradient(to right, currentColor 1px, transparent 1px),
            linear-gradient(to bottom, currentColor 1px, transparent 1px)
          `,
          backgroundSize: '80px 80px',
        }}
      />

      {/* Floating code fragments */}
      {codeItems.map(item => (
        <div
          key={item.id}
          className="absolute font-mono text-sm text-accent whitespace-nowrap"
          style={{
            left: `${item.x}%`,
            top: `${item.y}%`,
            opacity: item.opacity,
            transform: 'translate(-50%, -50%)',
          }}
        >
          {item.text}
        </div>
      ))}

      {/* SVG for particles and connections */}
      <svg className="absolute inset-0 w-full h-full">
        {/* Connections */}
        {connections.map((conn, i) => (
          <line
            key={i}
            x1={`${conn.x1}%`}
            y1={`${conn.y1}%`}
            x2={`${conn.x2}%`}
            y2={`${conn.y2}%`}
            stroke="#6366f1"
            strokeWidth="1"
            opacity={conn.opacity}
          />
        ))}

        {/* Particles */}
        {particles.map(p => (
          <circle
            key={p.id}
            cx={`${p.x}%`}
            cy={`${p.y}%`}
            r={p.size}
            fill={p.color}
            opacity={p.opacity}
          />
        ))}
      </svg>

      {/* Animated gradient orbs */}
      <div className="absolute top-1/4 left-1/4 w-32 h-32 rounded-full bg-accent/10 blur-3xl animate-pulse" />
      <div className="absolute bottom-1/3 right-1/4 w-40 h-40 rounded-full bg-purple-500/10 blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      <div className="absolute top-1/2 right-1/3 w-24 h-24 rounded-full bg-blue-500/10 blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />

      {/* Scan line effect */}
      <div
        className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/20 to-transparent"
        style={{
          top: `${(Date.now() / 50) % 100}%`,
          animation: 'scan 8s linear infinite',
        }}
      />

      <style>{`
        @keyframes scan {
          0% { top: -5%; opacity: 0; }
          5% { opacity: 1; }
          95% { opacity: 1; }
          100% { top: 105%; opacity: 0; }
        }
      `}</style>
    </div>
  );
}
